import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { translate } from "../../../translations";
import Tablist from "../../../components/tablist";
import {
  OpinionOption,
  Party,
  SKIP_VALUE,
  Statement,
  Stored,
  StudentOpinion,
  PopulatedVotingSession,
} from "../../../types";
import { compareStrings } from "../../../utils/comparators";
import PartyInfo from "../../../components/party_info";
import { Link } from "react-router";
import APIclient from "../../../api-client";

enum TABS {
  STATEMENTS,
  PARTIES,
}

export default function Results({
  votingSession,
  answers,
}: {
  votingSession: PopulatedVotingSession;
  answers: Array<StudentOpinion>;
}) {
  const [parties, setParties] = useState<Array<Stored<Party>>>([]);
  const [currentTab, setCurrentTab] = useState(TABS.STATEMENTS);

  useEffect(() => {
    APIclient.sessions
      .parties(votingSession.code)
      .then((parties) =>
        setParties(parties.sort((p1, p2) => compareStrings(p1.name, p2.name))),
      );
  }, [votingSession]);

  async function toggleShowResults(event: ChangeEvent<HTMLInputElement>) {
    APIclient.sessions.update(votingSession.code, {
      showResults: event.target.checked,
    });
  }

  return (
    <>
      <h1 className="voting-session__title" id="resultsTitle">
        <span className="--highlighted">
          {translate("teacher.votingSession.results.title")}
        </span>
      </h1>

      <Tablist
        className="voting-session__tablist"
        ariaLabelledBy="resultsTitle"
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        options={[
          {
            value: TABS.STATEMENTS,
            controls: "resultsByStatement",
            text: translate("teacher.votingSession.results.byStatement"),
            className: "voting-session__tab",
          },
          {
            value: TABS.PARTIES,
            controls: "resultsByParty",
            text: translate("teacher.votingSession.results.partyInfo"),
            className: "voting-session__tab",
          },
        ]}
      />

      <label className="voting-session__toggle toggle">
        <span className="toggle__label">
          {translate("teacher.votingSession.results.showResults")}
        </span>
        <div className="toggle__switch-wrapper">
          <input
            type="checkbox"
            name="showResults"
            id="showResults"
            role="switch"
            className="toggle__input"
            onChange={toggleShowResults}
            defaultChecked={votingSession.showResults}
          />
          <div className="toggle__switch"></div>
        </div>
      </label>

      {currentTab === TABS.STATEMENTS && (
        <StatementsWithResult
          statements={votingSession.statements}
          answers={answers}
        />
      )}
      {currentTab === TABS.PARTIES && (
        <div className="voting-session__content" id="resultsByParty">
          {parties.map((party) => (
            <PartyInfo party={party} key={party._id} />
          ))}
        </div>
      )}
    </>
  );
}

function StatementsWithResult({
  statements,
  answers,
}: {
  statements: Array<Stored<Statement>>;
  answers: Array<StudentOpinion>;
}) {
  return (
    <ol
      className="voting-session__content voting-session__content--compact"
      id="resultsStatements"
    >
      {statements.map((statement, index) => (
        <StatementWithAnswer
          statement={statement}
          answers={answers}
          index={index}
          key={statement._id}
        />
      ))}
    </ol>
  );
}

/* NOTE: This is only exported for testing purposes */
export function StatementWithAnswer({
  statement,
  answers,
  index,
}: {
  statement: Stored<Statement>;
  answers: Array<StudentOpinion>;
  index: number;
}) {
  const optionsWithPercentage = useMemo(() => {
    const counts = answers.reduce<Record<string, number>>((acc, cur) => {
      if (cur.statementId !== statement._id || cur.opinion === SKIP_VALUE) {
        return acc;
      }

      acc[cur.opinion] = (acc[cur.opinion] || 0) + 1;
      return acc;
    }, {});
    const total = Object.values(counts).reduce((acc, cur) => acc + cur, 0);
    return (
      statement.opinionOptions as Array<OpinionOption & { percentage: number }>
    )
      .sort((o1, o2) => o1.order - o2.order)
      .map((opt) => {
        opt.percentage = counts[opt.opinion] / total || 0;
        return opt;
      });
  }, [answers, statement]);
  const linkRef = useRef<HTMLAnchorElement>(null);

  return (
    <li
      className="statement-result"
      key={statement._id}
      onClick={() => linkRef.current?.click()}
    >
      <h2 className="statement-result__title">
        <span className="statement-result__index">{index + 1}.</span>
        {statement.description}
      </h2>
      <div className="statement-result__details">
        <div className="statement-result__distribution answer-distribution">
          <div className="answer-distribution__bar">
            {optionsWithPercentage.map((option) => (
              <span
                className="answer-distribution__part"
                key={option.opinion}
                data-option={option.opinion}
                style={{
                  "--option-color": option.color,
                  "--width": option.percentage,
                }}
              ></span>
            ))}
          </div>
          <div className="answer-distribution__legend-wrapper">
            {optionsWithPercentage.map((option) => (
              <span
                className="answer-distribution__legend"
                key={option.opinion}
              >
                {Math.round(option.percentage * 100)}% {option.opinion}
              </span>
            ))}
          </div>
        </div>
        <Link
          className="statement-result__link link"
          to={`${index + 1}`}
          ref={linkRef}
        >
          {translate("teacher.votingSession.results.openStatement")}
        </Link>
      </div>
    </li>
  );
}
